import React from "react";

const LnoFrameworkVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
        This lesson shows why strong performers stall when effort goes to the wrong work. It introduces the L N O framework to sort tasks into leverage, neutral, and overhead so delegation becomes strategic. Leverage work creates visible scale and signals judgment, neutral work protects credibility, overhead work should be automated or handed off. The goal is to shift time toward leverage, keep neutral steady, and shrink overhead through regular recalibration.
      </p>
    </div>
  );

const transcript = (
  <div className="pt-2 pb-6 space-y-4  overflow-y-scroll font-gotham text-body leading-7 text-[#323232]">
    <p>
      If you’re a solid performer but not moving up, it’s rarely a problem of
      effort, it’s about where that effort goes.
    </p>

    <p>
      That’s where leverage comes in, knowing when to do the work yourself and
      when to delegate it to your team.
    </p>

    <p>
      Some tasks feel like only you can do them. Others seem too simple to
      deserve your time.
    </p>

    <p>
      But here’s the funny part, many of us secretly like being busy. We complain
      about packed calendars and endless meetings, but deep down, that busyness
      makes us feel important, needed, and in control.
    </p>

    <p>
      It’s comforting to believe that everything will fall apart without us.
      But that’s not leadership, that’s workload addiction.
    </p>

    <p>
      So, how do you actually know which tasks to own, which to guide, and which
      to let go of?
    </p>

    <p>That’s where the L-N-O Framework comes in.</p>

    <p>
      Introduced by Shreyas Doshi, a renowned product and leadership thinker,
      this framework helps you sort every task into one of three buckets:
      Leverage, Neutral, or Overhead. Once you start doing that, delegation
      stops being random, it becomes deliberate, strategic, and career-defining.
    </p>

    <ul className="list-disc pl-6 space-y-2">
      <li>
        <strong>Leverage tasks (L):</strong>
        <br />
        These are your 10x tasks, they create massive, visible impact. They
        deserve your best effort. Think leading a project that saves your team
        200 hours a quarter, or fixing a cross-team bottleneck everyone’s been
        tolerating for months.
        <br />
        <br />
        These tasks signal judgment, visibility, and scale, everything promotion
        panels look for. Do these personally, and do them exceptionally well.
        <br />
        <br />
        You own these tasks, but you don’t do every detail. Your job is to set
        direction, own the decision, and protect the narrative. Delegate
        subtasks, pilots, and execution steps, but keep the final judgment.
      </li>

      <li>
        <strong>Neutral tasks (N):</strong>
        <br />
        These are the routine but necessary tasks, weekly reports, sprint
        reviews, team syncs. They keep the machine running. They don’t make you
        promotable, but neglecting them can hurt your credibility.
        <br />
        <br />
        Delegate these with autonomy and structure. Give your team clear
        ownership, an outcome metric and a cadence of checkpoints. Be there only
        when they’re stuck, no judgment, no micromanaging. Here, your goal is
        reliability, not perfection.
      </li>

      <li>
        <strong>Overhead tasks (O):</strong>
        <br />
        Expense forms, scheduling, messenger replies, stuff that needs to be
        done but adds almost no value.
        <br />
        <br />
        <em>Delegate or automate these ruthlessly.</em> Even if the output is
        80% as good, it’s worth it. Because every minute you spend here is a
        minute stolen from your Leverage time.
      </li>
    </ul>

    <p>
      Now how do you classify a task into these three buckets
      <br />
      Ask yourself two questions: How big is the impact? And how much effort
      does it deserve for optimal return?
    </p>

    <p>
      If it creates multiplicative outcomes — it’s L.
      <br />
      Eg: <strong>Researching your boss’s priorities</strong> so you can align
      your work with what <em>their</em> boss cares about.
    </p>

    <p>
      If it keeps operations steady — it’s N.
      <br />
      Eg: Being “dependable” but not necessarily <em>impactful</em>.
    </p>

    <p>
      If it’s low return and clerical — it’s O.
      <br />
      Eg: Volunteering for admin work because “someone needed to do it.”
    </p>

    <p>
      Does that mean we spend all our time in Leverage tasks and delegate the
      rest?
    </p>

    <p>
      No, promotable people maintain a balance. Ideally, 60–70% time should go
      for L tasks, 25–35% for N tasks and only 5% or less for O tasks.
    </p>

    <p>That mix keeps you visible, scalable, and sane.</p>

    <p>
      So, take a look at your task list today. Can you tell your L, N, and O
      apart?
    </p>

    <p>
      Make this your quarterly ritual. Because what’s Leverage today might be
      Neutral tomorrow. Keep revisiting. Keep recalibrating. And tie your
      reputation, not to how full your calendar looks but to how much Leverage
      your work creates.
    </p>

    <p>
      Are you working hard — or working in <em>Leverage</em>?
    </p>
  </div>
);

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  return { about, transcript };
};

export default LnoFrameworkVideo;
